import {
  Breakpoint,
  Color,
  DragAndDropMatcher,
  FontWeight,
  FormButton,
  FormButtonTheme,
  Typography,
  TypographyVariant,
  UseState,
} from '@elearning-platform/ui';
import { useMediaQuery } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { InteractionImage } from '../../../../enums';
import { DragAndDropFeedback, DragAndDropPair, QuizFeedback } from '../../interfaces';
import { QuizResultScreen } from '../quizResultScreen/quizResultScreen.component';

import { ConfirmButtonWrapper, DescriptionWrapper, HeaderContainer, Wrapper } from './dragAndDrop.styled';
import { DragAndDropProps } from './dragAndDrop.types';

export const DragAndDrop: React.FC<DragAndDropProps> = (props: DragAndDropProps): JSX.Element => {
  const { content, callComplete }: DragAndDropProps = props;
  const { t }: TransProps<never> = useTranslation();
  const isSmallDesktop: boolean = useMediaQuery(Breakpoint.SmallDesktop);
  const [attempts, setAttempts]: UseState<number> = useState<number>(0);
  const [isSubmitted, setIsSubmitted]: UseState<boolean> = useState<boolean>(false);
  const [feedback, setFeedback]: UseState<DragAndDropFeedback[]> = useState<DragAndDropFeedback[]>([]);
  const [isConfirmButtonDisabled, setIsConfirmButtonDisabled]: UseState<boolean> = useState<boolean>(true);
  const [selectedPairs, setSelectedPairs]: UseState<Record<string, string>> = useState<Record<string, string>>(
    (): Record<string, string> => {
      const shuffledIds: string[] = content.pairs.map((pair: DragAndDropPair): string => pair.id).sort((): number => Math.random() - 0.5);

      return content.pairs.reduce(
        (acc: Record<string, string>, pair: DragAndDropPair, index: number): Record<string, string> => {
          acc[pair.id] = shuffledIds[index];

          return acc;
        }, {}
      );
    }
  );

  const isCorrect: boolean = useMemo((): boolean => (
    Object.entries(selectedPairs).every(([definitionId, blockId]: [string, string]): boolean => definitionId === blockId)
  ), [selectedPairs]);

  const headline: string = useMemo((): string => {
    if (isCorrect) {
      return t('competence.app.scrollLesson.componentsV2.dragAndDrop.allQuestionsCorrect');
    } else if (attempts === 1 && !isCorrect) {
      return t('competence.app.scrollLesson.componentsV2.dragAndDrop.incorrectAnswersCheck');
    } else {
      return t('competence.app.scrollLesson.componentsV2.dragAndDrop.incorrectAnswers');
    }
  }, [isCorrect, attempts, t]);

  const handleSubmit: () => void = (): void => {
    if (attempts === 1 || isCorrect) {
      const result: DragAndDropFeedback[] = content.pairs.map((pair: DragAndDropPair): DragAndDropFeedback => ({
        definition: pair.definition,
        explanation: pair.answerExplanation ?? '',
        correctAnswer: pair.blockLabel,
        userAnswer: content.pairs.find((p: DragAndDropPair): boolean => p.id === selectedPairs[pair.id])?.blockLabel ?? '',
        isCorrect: selectedPairs[pair.id] === pair.id,
      }));
      setFeedback(result);
      setIsSubmitted(true);
      callComplete();

      return;
    }
    setIsSubmitted(true);
  };

  const handleRetry: () => void = (): void => {
    setIsSubmitted(false);
    setAttempts(1);
    setFeedback([]);
    setIsConfirmButtonDisabled(true);
  };

  const mapFeedbackToResult: QuizFeedback[] = feedback.map((feedbackItem: DragAndDropFeedback): QuizFeedback => ({
    additionalInfo: feedbackItem.explanation,
    question: feedbackItem.definition,
    correctAnswers: feedbackItem.isCorrect
      ? feedbackItem.correctAnswer
      : `~${feedbackItem.userAnswer}~ &rarr; ${feedbackItem.correctAnswer} &check;`,
    isCorrect: feedbackItem.isCorrect,
  }));

  return (
    <Wrapper>
      {!isSubmitted ? (
        <>
          <HeaderContainer>
            <Typography fontWeight={FontWeight.SemiBold} variant={TypographyVariant.TextXL}>
              {content.header}
            </Typography>
            <DescriptionWrapper>
              <Typography color={Color.Gray600} variant={TypographyVariant.TextXS}>
                {content.description}
              </Typography>
            </DescriptionWrapper>
          </HeaderContainer>
          <DragAndDropMatcher
            onSelectedPairsChange={(matches: Record<string, string>): void => {
              setSelectedPairs(matches);
              setIsConfirmButtonDisabled(false);
            }}
            pairs={content.pairs}
            selectedPairs={selectedPairs}
          />
          <ConfirmButtonWrapper>
            <FormButton
              disabled={isConfirmButtonDisabled}
              fullWidth={!isSmallDesktop}
              label={t('competence.app.scrollLesson.componentsV2.dragAndDrop.confirm')}
              onClick={handleSubmit}
              variant={FormButtonTheme.SecondaryBrand}
            />
          </ConfirmButtonWrapper>
        </>
      ) : (
        <QuizResultScreen
          feedback={mapFeedbackToResult}
          headline={headline}
          image={isCorrect ? InteractionImage.DragAndDropPositiveResult : InteractionImage.DragAndDropNegativeResult}
          isFinalResult={feedback.length > 0}
          isPassed={isCorrect}
          isSubmitted={isSubmitted}
          onButtonClick={handleRetry}
          supportingText={t('competence.app.scrollLesson.componentsV2.dragAndDrop.retryAllQuestions')}
        />
      )}
    </Wrapper>
  );
};
